import CITY_DATA from "../assets/data/cityData.json";
import POSTAL_DATA from "../assets/data/postalData.json";

const SWISS_LOCALITIES_API_URL = "https://openplzapi.org/ch/Localities";

export function getCityFromPostal(postalValue: string): string | undefined {
  if (!postalValue || postalValue.trim().length < 4) {
    return undefined;
  }

  return (POSTAL_DATA as Record<string, string>)[postalValue];
}

export function getCanonicalCityName(cityValue: string): string | undefined {
  if (!cityValue || cityValue.trim().length === 0) {
    return undefined;
  }

  const cityData = CITY_DATA as Record<string, string>;
  if (cityData[cityValue]) {
    return cityValue;
  }

  const lowerCityValue = cityValue.toLowerCase();
  return Object.keys(cityData).find((key) => key.toLowerCase() === lowerCityValue);
}

export function getPostalFromCity(cityValue: string): string | undefined {
  const canonicalCityName = getCanonicalCityName(cityValue);
  return canonicalCityName ? (CITY_DATA as Record<string, string>)[canonicalCityName] : undefined;
}

interface OpenPlzLocalityResponse {
  name?: string;
  Name?: string;
  postalcode?: string;
  postalCode?: string;
  PostalCode?: string;
  commune?: {
    name?: string;
    Name?: string;
    shortName?: string;
    ShortName?: string;
  };
  Commune?: {
    name?: string;
    Name?: string;
    shortName?: string;
    ShortName?: string;
  };
  canton?: {
    shortName?: string;
    ShortName?: string;
    name?: string;
    Name?: string;
  };
  Canton?: {
    shortName?: string;
    ShortName?: string;
    name?: string;
    Name?: string;
  };
}

export interface SwissCantonLookupResult {
  code?: string;
  name?: string;
}

function getOpenPlzLocalityName(locality: OpenPlzLocalityResponse): string | undefined {
  return locality.name ?? locality.Name;
}

function getOpenPlzLocalityPostalCode(locality: OpenPlzLocalityResponse): string | undefined {
  return locality.postalcode ?? locality.postalCode ?? locality.PostalCode;
}

function getOpenPlzCommune(locality: OpenPlzLocalityResponse) {
  return locality.commune ?? locality.Commune;
}

function getOpenPlzCanton(locality: OpenPlzLocalityResponse) {
  return locality.canton ?? locality.Canton;
}

function getOpenPlzCommuneNames(locality: OpenPlzLocalityResponse): string[] {
  const commune = getOpenPlzCommune(locality);
  return [commune?.name, commune?.Name, commune?.shortName, commune?.ShortName].filter(
    (value): value is string => Boolean(value),
  );
}

export async function getSwissCantonFromPostalAndCity(
  postalValue: string,
  cityValue: string,
  signal?: AbortSignal,
): Promise<SwissCantonLookupResult | undefined> {
  if (!postalValue || postalValue.trim().length < 4) {
    return undefined;
  }

  const canonicalCityName = getCanonicalCityName(cityValue);
  if (!canonicalCityName) {
    return undefined;
  }

  const params = new URLSearchParams({
    postalCode: postalValue.trim(),
    name: canonicalCityName,
  });

  const response = await fetch(`${SWISS_LOCALITIES_API_URL}?${params.toString()}`, { signal });
  if (!response.ok) {
    throw new Error("Swiss locality lookup failed");
  }

  const data = (await response.json()) as OpenPlzLocalityResponse[];
  const matchingLocalities = data.filter(
    (locality) =>
      getOpenPlzLocalityPostalCode(locality) === postalValue.trim() &&
      getOpenPlzLocalityName(locality)?.toLowerCase() === canonicalCityName.toLowerCase(),
  );

  if (matchingLocalities.length === 0) {
    return undefined;
  }

  const communeMatchedLocalities = matchingLocalities.filter((locality) =>
    getOpenPlzCommuneNames(locality).some(
      (communeName) => communeName.toLowerCase() === canonicalCityName.toLowerCase(),
    ),
  );

  const selectedLocality =
    communeMatchedLocalities.length === 1
      ? communeMatchedLocalities[0]
      : matchingLocalities.length === 1
        ? matchingLocalities[0]
        : undefined;

  if (!selectedLocality) {
    return undefined;
  }

  const canton = getOpenPlzCanton(selectedLocality);
  return {
    code: canton?.shortName ?? canton?.ShortName,
    name: canton?.name ?? canton?.Name,
  };
}
